// src/components/RealEngineHealthWidget.tsx - MOBILE RESPONSIVE
import React from 'react';
import { Box, Typography, LinearProgress, Chip, Stack, useTheme, useMediaQuery } from '@mui/material';
import { CheckCircle, Warning, Error } from '@mui/icons-material';

interface Props {
  data: any[];
}

const RealEngineHealthWidget: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!data || data.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No engine health data available
        </Typography>
      </Box>
    );
  }

  const summary = data.reduce((acc, vehicle) => {
    acc[vehicle.status] = (acc[vehicle.status] || 0) + 1;
    acc.totalScore += vehicle.healthScore;
    return acc;
  }, { healthy: 0, warning: 0, critical: 0, totalScore: 0 });

  const avgScore = Math.round(summary.totalScore / data.length);

  // Top 3 critical vehicles
  const criticalVehicles = data
    .filter(v => v.status === 'critical' || v.status === 'warning')
    .sort((a, b) => a.healthScore - b.healthScore)
    .slice(0, isMobile ? 2 : 3);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Overall Health Score */}
      <Box>
        <Typography 
          variant={isMobile ? 'h4' : 'h3'} 
          sx={{ fontWeight: 'bold', mb: 1 }}
        >
          {avgScore}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Average Health Score
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={avgScore} 
          sx={{ 
            height: isMobile ? 6 : 8, 
            borderRadius: 4,
            backgroundColor: '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: avgScore >= 80 ? theme.palette.success.main : avgScore >= 60 ? theme.palette.warning.main : theme.palette.error.main,
              borderRadius: 4,
            }
          }}
        />
      </Box>

      {/* Status Distribution */}
      <Stack 
        direction={isMobile ? 'column' : 'row'} 
        spacing={1} 
        flexWrap="wrap"
        sx={{ gap: 1 }}
      >
        <Chip 
          icon={<CheckCircle />}
          label={`${summary.healthy || 0} Healthy`}
          color="success"
          size="small"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        />
        <Chip 
          icon={<Warning />}
          label={`${summary.warning || 0} Warning`}
          color="warning"
          size="small"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        />
        <Chip 
          icon={<Error />}
          label={`${summary.critical || 0} Critical`}
          color="error"
          size="small"
          sx={{ fontSize: isMobile ? 11 : 13 }}
        />
      </Stack>

      {/* Critical Vehicles List */}
      {criticalVehicles.length > 0 && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Typography 
            variant="body2" 
            sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? 12 : 14 }}
          >
            Needs Attention:
          </Typography>
          {criticalVehicles.map((vehicle, idx) => (
            <Box 
              key={idx} 
              sx={{ 
                mb: 1, 
                p: isMobile ? 0.75 : 1, 
                bgcolor: '#fff3e0',
                borderRadius: 1,
                borderLeft: `4px solid ${vehicle.status === 'critical' ? theme.palette.error.main : theme.palette.warning.main}`
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500,
                  fontSize: isMobile ? 12 : 14 
                }}
              >
                {vehicle.device?.plateNumber || vehicle.imei}
              </Typography>
              <Typography 
                variant="caption" 
                color="textSecondary"
                sx={{ fontSize: isMobile ? 10 : 12 }}
              >
                Score: {vehicle.healthScore} - {vehicle.issues.length} issue(s)
              </Typography>
              {vehicle.issues.slice(0, 1).map((issue: any, i: number) => (
                <Typography 
                  key={i} 
                  variant="caption" 
                  display="block" 
                  color="error.main"
                  sx={{ 
                    fontSize: isMobile ? 10 : 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {issue.message}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RealEngineHealthWidget;